import {
  DescriptionRunResultDTO,
  GetDescriptionRunEventsActionEnum,
  ListDescriptionRunsStatusEnum,
  PaginatedDescriptionRunEventsDTO,
  PaginatedDescriptionRunsDTO,
  PlatformDescriptionStatusDTO,
  ProductDescriptionSettingsDTO,
  ProductDescriptionsApi,
  RegeneratedDescriptionDTO
} from "@app/api/generated";
import { getAuthorizedApiConfig } from "@app/util/api-config";

/** Thin wrappers around the generated ProductDescriptionsApi, authorized like the rest of the admin. */

export const RUNS_PAGE_SIZE = 50;
export const EVENTS_PAGE_SIZE = 50;

interface IListRunsParams {
  page: number;
  platformId?: number;
  status?: ListDescriptionRunsStatusEnum;
}

interface IListEventsParams {
  runId: number;
  page: number;
  action?: GetDescriptionRunEventsActionEnum;
}

const getApi = async (): Promise<ProductDescriptionsApi> => new ProductDescriptionsApi(await getAuthorizedApiConfig());

export const getPlatformStatuses = async (): Promise<PlatformDescriptionStatusDTO[]> => {
  const api = await getApi();
  return api.getDescriptionPlatformStatuses();
};

export const listRuns = async ({ page, platformId, status }: IListRunsParams): Promise<PaginatedDescriptionRunsDTO> => {
  const api = await getApi();
  return api.listDescriptionRuns({
    limit: RUNS_PAGE_SIZE,
    offset: (page - 1) * RUNS_PAGE_SIZE,
    platformId,
    status
  });
};

/**
 * Start a run. Without a platformId the ESB runs every enabled platform. An admin trigger comes
 * back as soon as the run row exists and the batch carries on server-side, so no limit is passed:
 * the ESB's own maximum per run applies. A second trigger for a platform that is still running is
 * refused with a 409.
 */
export const runGeneration = async (platformId?: number): Promise<DescriptionRunResultDTO[]> => {
  const api = await getApi();
  return api.runGeneration({ platformId });
};

/** Regenerate one product. `force` overwrites a hand-written description. */
export const regenerateForProduct = async (productId: number, force = false): Promise<RegeneratedDescriptionDTO> => {
  const api = await getApi();
  return api.regenerateForProduct({ productId, force });
};

/** The prompt is one setting for every platform (BF-5072). */
export const getDescriptionSettings = async (): Promise<ProductDescriptionSettingsDTO> => {
  const api = await getApi();
  return api.getDescriptionSettings();
};

export const saveDescriptionSettings = async (
  settings: ProductDescriptionSettingsDTO
): Promise<ProductDescriptionSettingsDTO> => {
  const api = await getApi();
  return api.updateDescriptionSettings({ productDescriptionSettingsDTO: settings });
};

export const setPlatformEnabled = async (
  platformId: number,
  enabled: boolean
): Promise<PlatformDescriptionStatusDTO> => {
  const api = await getApi();
  return api.setDescriptionPlatformEnabled({ platformId, toggleProductDescriptionEnabledDTO: { enabled } });
};

/** A single run by id, for the detail page. */
export const getRunById = async (runId: number): Promise<DescriptionRunResultDTO> => {
  const api = await getApi();
  return api.getDescriptionRunById({ runId });
};

/** A page of a run's per-product rows, optionally filtered by outcome. */
export const getRunEvents = async ({
  runId,
  page,
  action
}: IListEventsParams): Promise<PaginatedDescriptionRunEventsDTO> => {
  const api = await getApi();
  return api.getDescriptionRunEvents({
    runId,
    limit: EVENTS_PAGE_SIZE,
    offset: (page - 1) * EVENTS_PAGE_SIZE,
    action
  });
};
